import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import Header from "@/components/Header";
import BottomBar from "@/components/BottomBar";
import CountrySelectDialog, {
  countries,
} from "@/components/CountrySelectDialog";
import {
  CheckCircle,
  Loader2,
  MapPin,
  Edit,
  ChevronDown,
  AlertCircle,
  ExternalLink,
} from "lucide-react";
import { oxpayAPI } from "@/lib/oxpay";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "react-toastify";

export default function InputAddress() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  const [accountData, setAccountData] = useState(null);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [countryDialogOpen, setCountryDialogOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [addressData, setAddressData] = useState({
    type_address: "residential",
    address_1: "",
    address_2: "",
    address_3: "",
    address_4: "",
    city: "",
    state: "",
    country: "",
    country_code: "",
    zipcode: "",
  });
  const [originalAddressData, setOriginalAddressData] = useState(null);
  const [addressCompleted, setAddressCompleted] = useState(false);
  const [eddRequired, setEddRequired] = useState(false);
  const [eddUrl, setEddUrl] = useState("");

  useEffect(() => {
    fetchAccountData();
  }, [user?.email]);

  useEffect(() => {
    // OxPay에서 주소 정보 가져오기
    if (accountData?.oxpay_user_id) {
      fetchUserAddress();
    }
  }, [accountData?.oxpay_user_id]);

  const fetchAccountData = async () => {
    if (!user?.email) return;

    try {
      const { data, error } = await supabase
        .from("accounts")
        .select("*")
        .eq("email", user.email)
        .single();

      if (data && !error) {
        setAccountData(data);

        // Check if address is already completed
        if (data.oxpay_address_completed) {
          setAddressCompleted(true);
          setIsEditingAddress(false);

          // Load existing address data from database
          setAddressData({
            type_address: "residential",
            address_1: data.address_line1 || "",
            address_2: data.address_line2 || "",
            address_3: "",
            address_4: "",
            city: data.address_city || "",
            state: data.address_state || "",
            country: data.address_country || "",
            country_code: "",
            zipcode: data.address_postal_code || "",
          });
          setOriginalAddressData({
            type_address: "residential",
            address_1: data.address_line1 || "",
            address_2: data.address_line2 || "",
            address_3: "",
            address_4: "",
            city: data.address_city || "",
            state: data.address_state || "",
            country: data.address_country || "",
            country_code: "",
            zipcode: data.address_postal_code || "",
          });

          // Set selected country if exists
          if (data.address_country) {
            const country = countries.find(
              (c) =>
                c.name === data.address_country ||
                c.iso3 === data.address_country
            );
            if (country) {
              setSelectedCountry(country);
            }
          }
        } else {
          setIsEditingAddress(true);
        }
      }
    } catch (error) {
      console.error("Error fetching account data:", error);
    }
  };

  const fetchUserAddress = async () => {
    setIsLoadingAddress(true);
    try {
      const response = await oxpayAPI.getUserAddress(accountData.oxpay_user_id);
      console.log("User address response:", response);

      if (response?.headers?.status_code !== "success") {
        setIsEditingAddress(true);
        return;
      }

      // Check if EDD is required
      if (
        response?.body?.message &&
        typeof response.body.message === "string"
      ) {
        const urlMatch = response?.body?.url.match(/https:\/\/[^\s]+/);
        if (urlMatch && response?.body?.url.includes("upload")) {
          setEddRequired(true);
          setEddUrl(urlMatch[0]);
          setIsEditingAddress(false);
          return;
        }
      }

      //
      if (response?.body) {
        const address = response.body;
        const fetchedAddress = {
          type_address: address.type_address || "residential",
          address_1: address.address_1 || "",
          address_2: address.address_2 || "",
          address_3: address.address_3 || "",
          address_4: address.address_4 || "",
          city: address.city || "",
          state: address.state || "",
          country: address.country || "",
          country_code: address.country_code || "",
          zipcode: address.zipcode || "",
        };
        setAddressData(fetchedAddress);
        setOriginalAddressData(fetchedAddress);

        // 국가 코드가 있으면 선택된 국가 설정
        if (address.country_code) {
          const country = countries.find(
            (c) => c.iso3 === address.country_code
          );
          if (country) {
            setSelectedCountry(country);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching user address:", error);
      // 에러가 발생해도 UI는 계속 표시
      setIsEditingAddress(true);
    } finally {
      setIsLoadingAddress(false);
    }
  };

  const handleAddressChange = (field, value) => {
    setAddressData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setAddressData((prev) => ({
      ...prev,
      country: country.name,
      country_code: country.iso3,
    }));
  };

  const isAddressComplete = () => {
    return (
      addressData.address_1.trim() !== "" &&
      addressData.city.trim() !== "" &&
      addressData.country.trim() !== "" &&
      addressData.zipcode.trim() !== ""
    );
  };

  const handleSaveAddress = async () => {
    if (!accountData?.oxpay_user_id) {
      toast.error("Please complete user registration first");
      navigate("/register-user");
      return;
    }

    // Debug logging
    console.log("Address data:", addressData);
    console.log("Address validation:", {
      address_1: addressData.address_1.trim() !== "",
      city: addressData.city.trim() !== "",
      country: addressData.country.trim() !== "",
      zipcode: addressData.zipcode.trim() !== "",
    });

    // Validate required fields
    if (!isAddressComplete()) {
      // More specific error message
      const missingFields = [];
      if (addressData.address_1.trim() === "")
        missingFields.push("Address Line 1");
      if (addressData.city.trim() === "") missingFields.push("City");
      if (addressData.country.trim() === "") missingFields.push("Country");
      if (addressData.zipcode.trim() === "") missingFields.push("Postal Code");

      toast.error(`Please fill in: ${missingFields.join(", ")}`);
      return;
    }

    // 길이 검증
    if (
      addressData.address_1.length > 35 ||
      addressData.address_2.length > 35 ||
      addressData.address_3.length > 35 ||
      addressData.address_4.length > 35
    ) {
      toast.error("Address lines must be 35 characters or less");
      return;
    }

    if (addressData.city.length > 20) {
      toast.error("City must be 20 characters or less");
      return;
    }

    if (addressData.state.length > 20) {
      toast.error("State must be 20 characters or less");
      return;
    }

    if (addressData.country.length > 50) {
      toast.error("Country must be 50 characters or less");
      return;
    }

    if (addressData.country_code.length > 3) {
      toast.error("Country code must be 3 characters or less");
      return;
    }

    setIsLoading(true);

    try {
      // Update address in OxPay
      const response = await oxpayAPI.updateUserAddress(
        accountData.oxpay_user_id,
        addressData
      );
      console.log("Address update response:", response);

      if (response?.headers?.status_code === "success") {
        // Update address in database
        const { error } = await supabase
          .from("accounts")
          .update({
            address_line1: addressData.address_1,
            address_line2: addressData.address_2,
            address_line3: addressData.address_3,
            address_line4: addressData.address_4,
            address_city: addressData.city,
            address_state: addressData.state,
            address_country: addressData.country,
            address_postal_code: addressData.zipcode,
            oxpay_address_completed: true,
            updated_at: new Date().toISOString(),
          })
          .eq("email", user?.email);

        if (error) {
          throw error;
        }

        toast.success("Address saved successfully!");
        setOriginalAddressData(addressData);
        setAddressCompleted(true);
        setIsEditingAddress(false);
      } else {
        throw new Error(
          response?.body?.description || "Failed to update address"
        );
      }
    } catch (error) {
      console.error("Error saving address:", error);
      toast.error(error.message || "Failed to save address");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setAddressData(
      originalAddressData || {
        type_address: "residential",
        address_1: "",
        address_2: "",
        address_3: "",
        address_4: "",
        city: "",
        state: "",
        country: "",
        country_code: "",
        zipcode: "",
      }
    );
    setIsEditingAddress(false);
  };

  const handleContinue = () => {
    navigate("/home");
  };

  return (
    <div className="min-h-screen bg-background">
      <Header showBack={true} onBackClick={() => navigate("/home")} />

      <div className="max-w-md mx-auto px-3 pb-20">
        {/* Title */}
        <div className="mt-6 mb-6">
          <h1 className="text-2xl font-bold">Input Address</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Enter your residential address information
          </p>
        </div>

        {/* Status Alert */}
        {/* {addressCompleted && !eddRequired && (
          <Alert className="mb-4 border-green-600 bg-green-50 dark:bg-green-950/20">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800 dark:text-green-200">
              Your address information has been saved successfully!
            </AlertDescription>
          </Alert>
        )} */}

        {/* Warning Message */}
        <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            ⚠️ Modifying and saving your profile will trigger a re-review
            process and may result in temporary account suspension.
          </p>
        </div>

        {/* EDD Required Card */}
        {eddRequired ? (
          <Card className="bg-card mb-4">
            <CardContent className="p-6">
              <div className="flex flex-col items-center gap-4 text-center">
                <div className="w-12 h-12 rounded-full bg-yellow-100 dark:bg-yellow-900/20 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-yellow-600 dark:text-yellow-500" />
                </div>
                <h3 className="font-semibold text-lg">
                  Additional Documentation Required
                </h3>
                <p className="text-sm text-muted-foreground">
                  To complete your address verification, you need to upload
                  additional documents for Enhanced Due Diligence (EDD).
                </p>
                <Button
                  onClick={() => window.open(eddUrl, "_blank")}
                  className="w-full max-w-xs"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Upload Documents
                </Button>
                <div className="space-y-1 mt-3">
                  <p className="text-xs text-muted-foreground">
                    You will be redirected to a secure page to upload your
                    documents.
                  </p>
                  <p className="text-xs font-medium text-muted-foreground">
                    ⚠️ You only need to upload these documents once.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          /* Address Information Card */
          <Card className="bg-card mb-4">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Address Information
                </h3>
                {!isEditingAddress && !isLoadingAddress && !eddRequired && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsEditingAddress(true)}
                  >
                    <Edit className="w-4 h-4 " />
                    Edit
                  </Button>
                )}
              </div>

              {/* Remove inline loading, will use dialog instead */}
              <div className="space-y-1.5">
                <div>
                  <label className="text-xs font-medium text-muted-foreground">
                    Address Line 1 *
                  </label>
                  <Input
                    type="text"
                    placeholder="Enter your street address"
                    value={addressData.address_1}
                    onChange={(e) =>
                      handleAddressChange("address_1", e.target.value)
                    }
                    disabled={!isEditingAddress}
                    maxLength={35}
                    className="mt-0.5 pl-2"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-muted-foreground">
                    Address Line 2
                  </label>
                  <Input
                    type="text"
                    placeholder="Apartment, suite, etc. (optional)"
                    value={addressData.address_2}
                    onChange={(e) =>
                      handleAddressChange("address_2", e.target.value)
                    }
                    disabled={!isEditingAddress}
                    maxLength={35}
                    className="mt-0.5 pl-2"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-muted-foreground">
                    Address Line 3
                  </label>
                  <Input
                    type="text"
                    placeholder="Additional address info (optional)"
                    value={addressData.address_3}
                    onChange={(e) =>
                      handleAddressChange("address_3", e.target.value)
                    }
                    disabled={!isEditingAddress}
                    maxLength={35}
                    className="mt-0.5 pl-2"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-muted-foreground">
                    Address Line 4
                  </label>
                  <Input
                    type="text"
                    placeholder="Additional address info (optional)"
                    value={addressData.address_4}
                    onChange={(e) =>
                      handleAddressChange("address_4", e.target.value)
                    }
                    disabled={!isEditingAddress}
                    maxLength={35}
                    className="mt-0.5 pl-2"
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">
                      City *
                    </label>
                    <Input
                      type="text"
                      placeholder="City"
                      value={addressData.city}
                      onChange={(e) =>
                        handleAddressChange("city", e.target.value)
                      }
                      disabled={!isEditingAddress}
                      maxLength={20}
                      className="mt-0.5 pl-2"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-medium text-muted-foreground">
                      State/Province
                    </label>
                    <Input
                      type="text"
                      placeholder="State"
                      value={addressData.state}
                      onChange={(e) =>
                        handleAddressChange("state", e.target.value)
                      }
                      disabled={!isEditingAddress}
                      maxLength={20}
                      className="mt-0.5 pl-2"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-muted-foreground">
                    Country *
                  </label>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setCountryDialogOpen(true)}
                    disabled={!isEditingAddress}
                    className="w-full mt-0.5 justify-between h-10 font-normal pl-2"
                  >
                    <div className="flex items-center gap-2">
                      {selectedCountry ? (
                        <>
                          <span className="text-lg">
                            {(() => {
                              const flags = {
                                AU: "🇦🇺",
                                CA: "🇨🇦",
                                CN: "🇨🇳",
                                FR: "🇫🇷",
                                DE: "🇩🇪",
                                HK: "🇭🇰",
                                ID: "🇮🇩",
                                JP: "🇯🇵",
                                MY: "🇲🇾",
                                NZ: "🇳🇿",
                                SG: "🇸🇬",
                                KR: "🇰🇷",
                                ES: "🇪🇸",
                                TW: "🇹🇼",
                                TH: "🇹🇭",
                                GB: "🇬🇧",
                                US: "🇺🇸",
                              };
                              return flags[selectedCountry.code] || "🏳️";
                            })()}
                          </span>
                          <span>{selectedCountry.name}</span>
                          <span className="text-muted-foreground">
                            ({selectedCountry.iso3})
                          </span>
                        </>
                      ) : (
                        <span className="text-muted-foreground">
                          Select country
                        </span>
                      )}
                    </div>
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </div>

                <div>
                  <label className="text-xs font-medium text-muted-foreground">
                    Zip/Postal Code *
                  </label>
                  <Input
                    type="text"
                    placeholder="Zip code"
                    value={addressData.zipcode}
                    onChange={(e) =>
                      handleAddressChange("zipcode", e.target.value)
                    }
                    disabled={!isEditingAddress}
                    className="mt-0.5 pl-2"
                  />
                </div>

                {isEditingAddress && (
                  <div className="flex gap-2 pt-1">
                    <Button
                      onClick={handleSaveAddress}
                      disabled={isLoading}
                      className="flex-1"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        "Save Address"
                      )}
                    </Button>
                    {originalAddressData && (
                      <Button
                        variant="outline"
                        onClick={handleCancelEdit}
                        disabled={isLoading}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Info Card */}
        {/* <Card className="bg-muted/50">
          <CardContent className="p-4">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
              <div className="text-xs">
                <p className="font-medium mb-1">Required Information</p>
                <ul className="space-y-1 text-muted-foreground">
                  <li>• Address Line 1 is required</li>
                  <li>• City is required</li>
                  <li>• Country is required</li>
                  <li>• Postal/Zip Code is required</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card> */}

        {/* Continue Button */}
        {!isEditingAddress && !eddRequired && (
          <div className="mt-6">
            <Button onClick={handleContinue} className="w-full h-12" size="lg">
              Continue
            </Button>
          </div>
        )}
      </div>

      <BottomBar />

      {/* Country Selection Dialog */}
      <CountrySelectDialog
        open={countryDialogOpen}
        onOpenChange={setCountryDialogOpen}
        onSelect={handleCountrySelect}
        selectedCountry={selectedCountry}
        showMobileCode={false}
        useIso3={true}
      />

      {/* Loading Dialog */}
      <Dialog open={isLoadingAddress} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md" hideClose>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading Address
            </DialogTitle>
            <DialogDescription>
              Loading your address information, please wait...
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </div>
  );
}
